<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <div class="page-title">마이페이지</div>
        <div class="page-subtitle">계정 정보는 조회만 가능하며 비밀번호만 변경할 수 있습니다.</div>
      </div>
    </div>

    <div class="content-wrapper">
      <div class="card mypage-card">
        <div class="mypage-form">
          <!-- 아이디 -->
          <div class="form-row">
            <label class="form-label">아이디</label>
            <div class="form-field readonly-text">
              {{ myInfo.accountId || '-' }}
            </div>
          </div>

          <!-- 소속 -->
          <div class="form-row">
            <label class="form-label">소속</label>
            <div class="form-field readonly-text affiliation-text">
              {{ resolvedAffiliationText }}
            </div>
          </div>

          <!-- 권한 -->
          <div class="form-row">
            <label class="form-label">권한</label>
            <div class="form-field readonly-text">
              {{ myInfo.roleName || '-' }}
            </div>
          </div>

          <!-- 현재 비밀번호 -->
          <div class="form-row">
            <label class="form-label required">현재 비밀번호</label>
            <div class="form-field password-field-wrap">
              <div class="password-input-wrap">
                <input
                  v-model="form.currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  class="control"
                  placeholder="현재 비밀번호를 입력해주세요."
                  @input="clearCurrentPasswordError"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showCurrentPassword = !showCurrentPassword"
                >
                  <i :class="showCurrentPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- 변경 비밀번호 -->
          <div class="form-row">
            <label class="form-label required">비밀번호 변경</label>
            <div class="form-field password-field-wrap">
              <div class="password-input-wrap">
                <input
                  v-model="form.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  class="control"
                  placeholder="변경할 비밀번호를 입력해주세요."
                  @input="validatePasswordMatch"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showNewPassword = !showNewPassword"
                >
                  <i :class="showNewPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- 비밀번호 확인 -->
          <div class="form-row form-row-top">
            <label class="form-label required">비밀번호 확인</label>
            <div class="form-field password-field-wrap">
              <div class="password-input-wrap error" :class="{ invalid: passwordMismatch }">
                <input
                  v-model="form.newPasswordConfirm"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="control"
                  placeholder="변경할 비밀번호를 다시 입력해주세요."
                  @input="validatePasswordMatch"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <i :class="showConfirmPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                </button>
              </div>

              <div v-if="passwordMismatch" class="field-error-text">
                비밀번호가 일치하지 않습니다.
              </div>
            </div>
          </div>

          <div class="submit-message">
            입력하신 새로운 비밀번호로 변경하시겠습니까?
          </div>

          <div class="submit-actions">
            <button
              class="btn primary submit-btn"
              type="button"
              :disabled="loading"
              @click="changePassword"
            >
              {{ loading ? '처리 중...' : '확인' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'MyPage',

  data() {
    return {
      loading: false,

      myInfo: {
        id: null,
        accountId: '',
        roleName: '',
        agencyName: '',
        clientName: '',
      },

      form: {
        currentPassword: '',
        newPassword: '',
        newPasswordConfirm: '',
      },

      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    }
  },

  computed: {
    authStore() {
      return useAuthStore()
    },

    loginUserId() {
      return this.authStore.user?.id ?? null
    },

    resolvedAffiliationText() {
      const agencyName = this.myInfo.agencyName?.trim()
      const clientName = this.myInfo.clientName?.trim()

      if (agencyName && clientName) {
        return `대리점: ${agencyName} / 발주처: ${clientName}`
      }

      if (agencyName) {
        return `대리점: ${agencyName}`
      }

      if (clientName) {
        return `발주처: ${clientName}`
      }

      return '-'
    },

    passwordMismatch() {
      if (!this.form.newPasswordConfirm) return false
      return this.form.newPassword !== this.form.newPasswordConfirm
    },
  },

  async mounted() {
    await this.fetchMyInfo()
  },

  methods: {
    async fetchMyInfo() {
      if (!this.loginUserId) {
        alert('로그인 사용자 정보를 찾을 수 없습니다.')
        return
      }

      try {
        const response = await api.get('/app/accountUser/detail', {
          params: {
            id: this.loginUserId,
          },
        })

        const result = response.data?.result

        this.myInfo = {
          id: result?.id ?? null,
          accountId: result?.accountId ?? '',
          roleName: result?.roleName ?? '',
          agencyName: result?.agencyName ?? '',
          clientName: result?.clientName ?? '',
        }
      } catch (error) {
        console.error('마이페이지 정보 조회 실패', error)
        alert(error?.response?.data?.message || '마이페이지 정보 조회에 실패했습니다.')
      }
    },

    validateForm() {
      if (!this.form.currentPassword?.trim()) {
        alert('현재 비밀번호를 입력해주세요.')
        return false
      }

      if (!this.form.newPassword?.trim()) {
        alert('변경할 비밀번호를 입력해주세요.')
        return false
      }

      if (!this.form.newPasswordConfirm?.trim()) {
        alert('비밀번호 확인을 입력해주세요.')
        return false
      }

      if (this.form.newPassword !== this.form.newPasswordConfirm) {
        alert('비밀번호가 일치하지 않습니다.')
        return false
      }

      if (this.form.currentPassword === this.form.newPassword) {
        alert('현재 비밀번호와 다른 비밀번호를 입력해주세요.')
        return false
      }

      return true
    },

    // 로그아웃 없이 유지하는 방식일 경우 사용 -> 지금은 변경 시 로그아웃 처리
    resetPasswordForm() {
      this.form.currentPassword = ''
      this.form.newPassword = ''
      this.form.newPasswordConfirm = ''
      this.showCurrentPassword = false
      this.showNewPassword = false
      this.showConfirmPassword = false
    },

    async changePassword() {
      if (!this.validateForm()) return

      const ok = window.confirm('입력하신 새로운 비밀번호로 변경하시겠습니까?')
      if (!ok) return

      try {
        this.loading = true

        await api.post('/app/accountUser/changePassword', {
          id: this.loginUserId,
          currentPassword: this.form.currentPassword,
          newPassword: this.form.newPassword,
          newPasswordConfirm: this.form.newPasswordConfirm,
        })

        // 로그아웃 없이 처리
        alert('비밀번호가 변경되었습니다.')
        this.resetPasswordForm()

        // 로그아웃 처리
        // alert('비밀번호가 변경되었습니다. 다시 로그인해주세요.')
        // this.authStore.logout()
        // this.$router.replace('/login')
        
      } catch (error) {
        console.error('비밀번호 변경 실패', error)
        alert(error?.response?.data?.message || '비밀번호 변경에 실패했습니다.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-input.css";
@import "@/styles/admin/admin-form.css";
@import "@/styles/admin/admin-card.css";

.mypage-card {
  max-width: 760px;
  margin: 0 auto;
  min-width: 0;
}

.mypage-form {
  padding: 40px 32px 32px;
}

.form-row {
  margin-bottom: 26px;
}

.form-row:last-of-type {
  margin-bottom: 0;
}

.form-row-top {
  align-items: flex-start;
}

.form-row-top .form-label {
  padding-top: 10px;
}

.readonly-text {
  min-height: 42px;
  display: flex;
  align-items: center;
  color: #111827;
  font-size: 15px;
  font-weight: 500;
}

.affiliation-text {
  line-height: 1.5;
  word-break: break-word;
}

.password-field-wrap {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.password-input-wrap {
  position: relative;
  width: 100%;
}

.password-input-wrap .control {
  padding-right: 46px;
}

.password-input-wrap.invalid .control {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.08);
}

.password-toggle-btn {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.password-toggle-btn:hover {
  color: #374151;
}

.field-error-text {
  color: #ef4444;
  font-size: 13px;
  line-height: 1.4;
}

.submit-message {
  margin-top: 36px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.submit-actions {
  margin-top: 18px;
  display: flex;
  justify-content: center;
}

.submit-btn {
  min-width: 130px;
}

@media (max-width: 768px) {
  .mypage-form {
    padding: 24px 18px;
  }

  .readonly-text {
    min-height: auto;
  }

  .submit-message {
    margin-top: 28px;
    font-size: 14px;
  }
}
</style>